import { yupResolver } from '@hookform/resolvers/yup';
import { checkboxClasses, typographyClasses } from '@mui/material';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import { ModelError_Item } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { Control, FieldErrors, useForm, useWatch } from 'react-hook-form';

import { PhoneCodeFlagInput } from '~components/FormFields/PhoneCodeFlagInput';
import CheckboxInput from '~components/FormFields/v2/CheckboxInput';
import TextInput from '~components/FormFields/v2/TextInput';
import ToggleButtonInput from '~components/FormFields/v2/ToggleButtonInput';
import { Account } from '~hooks/useAccount/models';
import { InviteType } from '~types/InviteTypes';

import {
  ConnectedAccountContactDto,
  connectedAccountContactSchema,
  setConnectedAccountContactSchemaDefaultValues,
} from './connectedAccountContactSchema';

const apiResponseErrorsMapping = {
  email: 'primaryContact.email',
  phone: 'primaryContact.phone',
} as const;

const inviteTypeOptions = [
  { label: t('enums.invite_type.email'), value: InviteType.EMAIL },
  { label: t('enums.invite_type.sms'), value: InviteType.SMS },
];

export type ConnectedAccountContactPanelHandler = {
  onSubmit: () => Promise<ConnectedAccountContactDto | undefined>;
};

interface ConnectedAccountContactPanelProps {
  account?: Account;
  apiResponseErrors?: ModelError_Item[];
}

const ConnectedAccountContactPanel = forwardRef<
  ConnectedAccountContactPanelHandler,
  ConnectedAccountContactPanelProps
>(function ConnectedAccountContactPanel({ apiResponseErrors, account }, ref) {
  const {
    clearErrors,
    control,
    formState: { errors },
    handleSubmit,
    setError,
    watch,
  } = useForm<ConnectedAccountContactDto>({
    resolver: yupResolver(connectedAccountContactSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: setConnectedAccountContactSchemaDefaultValues(account),
  });

  const isEditing = Boolean(account?.id);
  const isBillingContactSame = watch('isBillingContactSame');

  useEffect(() => {
    clearErrors();

    apiResponseErrors?.forEach((error) => {
      const apiField = error.field as keyof typeof apiResponseErrorsMapping;
      const formField = apiResponseErrorsMapping[apiField];

      if (formField) {
        setError(formField, { type: 'manual', message: error.message });
      }
    });
  }, [clearErrors, setError, apiResponseErrors]);

  useImperativeHandle(
    ref,
    () => ({
      onSubmit() {
        return new Promise((resolve) => {
          handleSubmit(
            (data) => {
              resolve(data);
            },
            () => {
              resolve(undefined);
            },
          )();
        });
      },
    }),
    [handleSubmit],
  );

  return (
    <>
      <ContactForm control={control} errors={errors} target="primaryContact" />

      {isEditing && (
        <Box sx={{ display: 'inline-block', mt: 2 }}>
          <CheckboxInput
            control={control}
            label={t('account.form.billing_contact_same_as_primary')}
            name="isBillingContactSame"
            sx={{
              [`& .${checkboxClasses.root}`]: { py: 0 },
              [`& .${typographyClasses.root}`]: { fontSize: '13px', fontWeight: 400 },
            }}
          />
        </Box>
      )}

      <Collapse in={!isBillingContactSame}>
        <Typography variant="form-section-title" sx={{ my: 2 }}>
          {t('account.form.billing_contact')}
        </Typography>

        <ContactForm control={control} errors={errors} target="billingContact" />
      </Collapse>
    </>
  );
});

interface ContactFormProps {
  control: Control<ConnectedAccountContactDto>;
  errors: FieldErrors<ConnectedAccountContactDto>;
  target: 'primaryContact' | 'billingContact';
}

function ContactForm({ control, errors, target }: ContactFormProps) {
  const isTargetingPrimaryContact = target === 'primaryContact';

  const isEditing = useWatch({ control, name: 'isEditing' });
  const selectedInviteTypes = useWatch({ control, name: 'inviteTypes' });

  const isEmailInviteSelected = selectedInviteTypes?.find((item) => {
    return item.value === InviteType.EMAIL;
  });
  const isSmsInviteSelected = selectedInviteTypes?.find((item) => {
    return item.value === InviteType.SMS;
  });

  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr', width: '100%' }}>
      <Box>
        <TextInput
          control={control}
          isRequired={isTargetingPrimaryContact}
          label={t('form_fields.first_name')}
          name={`${target}.firstName`}
        />
      </Box>

      <Box>
        <TextInput
          control={control}
          isRequired={isTargetingPrimaryContact}
          label={t('form_fields.last_name')}
          name={`${target}.lastName`}
        />
      </Box>

      {isTargetingPrimaryContact && !isEditing && (
        <Box sx={{ gridColumn: 'span 2' }}>
          <ToggleButtonInput
            control={control}
            isRequired
            label={t('account.form.invite_type')}
            name="inviteTypes"
            options={inviteTypeOptions}
          />
        </Box>
      )}

      <Box>
        <TextInput
          control={control}
          isRequired={Boolean(isTargetingPrimaryContact && isEmailInviteSelected)}
          label={t('form_fields.email')}
          name={`${target}.email`}
        />
      </Box>

      <Box>
        <PhoneCodeFlagInput
          control={control as unknown as Control}
          errors={errors}
          isRequired={Boolean(isTargetingPrimaryContact && isSmsInviteSelected)}
          label={t('form_fields.phone')}
          name={`${target}.phone`}
        />
      </Box>
    </Box>
  );
}

export default ConnectedAccountContactPanel;
